import { addExceptionNotification, addSuccess } from '@mse-reg/app/modules/notification.slice';
import { addCommentToRegistration, removeCommentFromRegistration } from '@mse-reg/app/store/reducer-helpers';
import {
  CommentDto,
  EmailDto,
  ExamRegistrationState,
  IEmailClient,
  IExamRegistrationClient,
  IRegistrationClient,
  IRegistrationEditClient,
  IStudentClient,
  ModuleRegistrationDto,
  ProfileDto,
  RegistrationStateChangeDto,
  resolver,
  SemesterDto,
  SemesterRegistrationDto,
  StudentDto,
  StudentType,
  UasDto,
} from '@mse-reg/clients';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { studentTabsModule } from './student-tabs.slice';

// State
const initialState = {
  studentLoading: false,
  loadedStudent: null as StudentDto | null,
  moduleRegistrationErrors: [] as { key: number; message: string }[],
};

export type StudentManagementState = Readonly<typeof initialState>;

// Slice
const slice = createSlice({
  name: 'studentManagement',
  initialState,
  reducers: {
    setStudentLoading(state, action: PayloadAction<boolean>) {
      state.studentLoading = action.payload;
    },
    setStudentInfo(state, action: PayloadAction<StudentDto | null>) {
      state.loadedStudent = action.payload ? { ...action.payload } : null;
    },
    addSemesterRegistration(state, action: PayloadAction<SemesterRegistrationDto>) {
      const semesterRegistration = action.payload;
      if (state.loadedStudent?.id === semesterRegistration?.studentId) {
        state.loadedStudent.semesterRegistrations = [
          ...(state.loadedStudent.semesterRegistrations ?? []),
          semesterRegistration,
        ];
      }
    },
    cancelAddingSemesterRegistration(state) {
      if (state.loadedStudent) {
        state.loadedStudent.semesterRegistrations = state.loadedStudent.semesterRegistrations?.filter(
          (s) => s.id !== 0,
        );
      }
    },
    updateSemesterRegistration(state, action: PayloadAction<SemesterRegistrationDto>) {
      const semesterRegistration = action.payload;
      if (state.loadedStudent?.id === semesterRegistration?.studentId) {
        state.loadedStudent.semesterRegistrations =
          state.loadedStudent.semesterRegistrations?.map((s) =>
            s.id === semesterRegistration.id ? { ...semesterRegistration } : s,
          ) ?? [];
      }
    },
    removeSemesterRegistration(state, action: PayloadAction<SemesterRegistrationDto>) {
      const semesterRegistration = action.payload;
      if (state.loadedStudent?.id === semesterRegistration?.studentId) {
        state.loadedStudent.semesterRegistrations =
          state.loadedStudent.semesterRegistrations?.filter((s) => s.id !== semesterRegistration.id) ?? [];
      }
    },
    addModuleRegistration(state, action: PayloadAction<ModuleRegistrationDto>) {
      const moduleRegistration = action.payload;
      if (
        moduleRegistration &&
        state.loadedStudent?.semesterRegistrations?.some((s) => s.id === moduleRegistration.semesterRegistrationId)
      ) {
        state.loadedStudent.semesterRegistrations = state.loadedStudent.semesterRegistrations.map((s) =>
          s.id === moduleRegistration.semesterRegistrationId
            ? { ...s, moduleRegistrations: [...(s.moduleRegistrations ?? []), moduleRegistration] }
            : s,
        );
      }
    },
    updateModuleRegistration(state, action: PayloadAction<ModuleRegistrationDto>) {
      const moduleRegistration = action.payload;
      if (
        moduleRegistration &&
        state.loadedStudent?.semesterRegistrations?.some((s) => s.id === moduleRegistration.semesterRegistrationId)
      ) {
        state.loadedStudent.semesterRegistrations = state.loadedStudent.semesterRegistrations.map((s) =>
          s.id === moduleRegistration.semesterRegistrationId
            ? {
                ...s,
                moduleRegistrations:
                  s.moduleRegistrations?.map((m) => (m.id === moduleRegistration.id ? moduleRegistration : m)) ?? [],
              }
            : s,
        );
      }
    },
    removeModuleRegistration(state, action: PayloadAction<ModuleRegistrationDto>) {
      const moduleRegistration = action.payload;
      if (
        moduleRegistration &&
        state.loadedStudent?.semesterRegistrations?.some((s) => s.id === moduleRegistration.semesterRegistrationId)
      ) {
        state.loadedStudent.semesterRegistrations = state.loadedStudent.semesterRegistrations.map((s) =>
          s.id === moduleRegistration.semesterRegistrationId
            ? { ...s, moduleRegistrations: s.moduleRegistrations?.filter((m) => m.id !== moduleRegistration.id) ?? [] }
            : s,
        );
      }
    },
    addRegistrationComment(state, action: PayloadAction<CommentDto>) {
      const comment = action.payload;
      if (state.loadedStudent) {
        state.loadedStudent.semesterRegistrations =
          state.loadedStudent.semesterRegistrations?.map((s) =>
            s.moduleRegistrations?.find((m) => m.id === comment.registrationId)
              ? { ...s, moduleRegistrations: addCommentToRegistration(s.moduleRegistrations, comment) }
              : s,
          ) ?? [];
      }
    },
    removeRegistrationComment(state, action: PayloadAction<CommentDto>) {
      const comment = action.payload;
      if (state.loadedStudent) {
        state.loadedStudent.semesterRegistrations =
          state.loadedStudent.semesterRegistrations?.map((s) =>
            s.moduleRegistrations?.find((m) => m.id === comment.registrationId)
              ? { ...s, moduleRegistrations: removeCommentFromRegistration(s.moduleRegistrations, comment) }
              : s,
          ) ?? [];
      }
    },
  },
});

export const studentManagementReducer = slice.reducer;
export const studentManagementActions = slice.actions;

// Component-scoped actions factory
type ThunkFn<T = void> = (dispatch: any, getState: () => any) => Promise<T>;
type SyncThunkFn<T = void> = (dispatch: any, getState: () => any) => T;

const initActions = (
  componentDispatch: any,
  studentManagementClient: IStudentClient,
  emailClient: IEmailClient,
  registrationEditClient: IRegistrationEditClient,
  registrationClient: IRegistrationClient,
  examRegistrationClient: IExamRegistrationClient,
  studentTabsActions: {
    selectStudent: (student: StudentDto) => void;
    removeTab: (student: StudentDto) => void;
  },
) => {
  const { actions } = slice;

  const setStudentInfo =
    (student: StudentDto | null): SyncThunkFn =>
    (dispatch) => {
      dispatch(actions.setStudentInfo(student));
      if (student) {
        studentTabsActions.selectStudent(student);
      }
    };

  const getHistoryAsync = async (id: number): Promise<RegistrationStateChangeDto[]> => {
    return await registrationClient.getHistory(id);
  };

  const getSemestersForNewRegistration = (
    semesters: SemesterDto[],
    semesterRegistrations: SemesterRegistrationDto[],
  ) => {
    const usedSemesters = new Set(semesterRegistrations.map((u) => u.semesterId));
    return semesters.filter((s) => !usedSemesters.has(s.id)).sort((s1, s2) => (s1.start > s2.start ? -1 : 1));
  };

  const getDefaultSemester = (semesters: SemesterDto[], semesterRegistrations: SemesterRegistrationDto[]) => {
    const possibleSemesters = getSemestersForNewRegistration(semesters, semesterRegistrations);
    return possibleSemesters[0];
  };

  const getLastRegistration = (semesterRegistrations: SemesterRegistrationDto[]) => {
    return semesterRegistrations.slice().sort((s1, s2) => (s1.semesterStart > s2.semesterStart ? -1 : 1))[0];
  };

  const generateNewSemesterRegistration = (
    student: StudentDto,
    semesters: SemesterDto[],
    uas: UasDto[],
    profiles: ProfileDto[],
  ): SemesterRegistrationDto => {
    const defaultSemester = getDefaultSemester(semesters, student.semesterRegistrations);
    const lastRegistration = getLastRegistration(student.semesterRegistrations);
    return {
      id: 0,
      isActive: true,
      semesterId: defaultSemester?.id ?? 0,
      registeredAtId: lastRegistration?.registeredAtId ?? uas[0]?.id,
      moduleRegistrations: [],
      examRegistrations: [],
      studentId: student.id,
      advisorId: lastRegistration?.advisorId ?? null,
      profileId: lastRegistration?.profileId ?? profiles[0]?.id,
      labelId: lastRegistration?.labelId ?? null,
      semesterStart: defaultSemester?.start,
      studentType: lastRegistration?.studentType ?? StudentType.Student,
      semester: undefined,
      registeredAt: undefined,
      profile: undefined,
      advisorName: undefined,
      label: undefined,
      studentFirstName: undefined,
      studentFamilyName: undefined,
    };
  };

  const generateNewStudent = (semesters: SemesterDto[], uas: UasDto[], profiles: ProfileDto[]): StudentDto => {
    const student: StudentDto = {
      id: '',
      firstName: '',
      familyName: '',
      hasHiddenInformation: false,
      previousDegree: '',
      matriculationNr: '',
      birthDate: null,
      semesterRegistrations: [],
      emails: [],
    };
    student.semesterRegistrations = [generateNewSemesterRegistration(student, semesters, uas, profiles)];
    return student;
  };

  const addNewSemesterRegistration =
    (
      student: StudentDto,
      semesters: SemesterDto[],
      uas: UasDto[],
      profiles: ProfileDto[],
    ): SyncThunkFn<SemesterRegistrationDto> =>
    (dispatch) => {
      const semesterRegistration = generateNewSemesterRegistration(student, semesters, uas, profiles);
      dispatch(actions.addSemesterRegistration(semesterRegistration));
      return semesterRegistration;
    };

  const addNewStudent =
    (semesters: SemesterDto[], uas: UasDto[], profiles: ProfileDto[]): SyncThunkFn<StudentDto> =>
    (dispatch) => {
      const student = generateNewStudent(semesters, uas, profiles);
      dispatch(setStudentInfo(student));
      return student;
    };

  const loadStudentAsync =
    (studentId: string): ThunkFn<StudentDto | null> =>
    async (dispatch) => {
      dispatch(actions.setStudentLoading(true));
      try {
        const studentInfo = await studentManagementClient.getStudent(studentId);
        dispatch(setStudentInfo(studentInfo));
        dispatch(actions.setStudentLoading(false));
        return studentInfo;
      } catch (e) {
        dispatch(addExceptionNotification(e, 'Error while loading student.'));
        dispatch(actions.setStudentLoading(false));
        return null;
      }
    };

  const deleteEmailAsync =
    (email: EmailDto): ThunkFn<StudentDto | null> =>
    async (dispatch) => {
      dispatch(actions.setStudentLoading(true));
      try {
        await emailClient.delete(email.id);
        const studentInfo = await studentManagementClient.getStudent(email.userId);
        dispatch(setStudentInfo(studentInfo));
        dispatch(addSuccess(`Successfully deleted the email ${email.name}.`));
        dispatch(actions.setStudentLoading(false));
        return studentInfo;
      } catch (e) {
        dispatch(addExceptionNotification(e, 'Error while deleting email.'));
        dispatch(actions.setStudentLoading(false));
        return null;
      }
    };

  const updateStudentAsync =
    (student: StudentDto): ThunkFn<StudentDto | null> =>
    async (dispatch) => {
      dispatch(actions.setStudentLoading(true));
      try {
        await studentManagementClient.updateStudent(student);
        const updatedStudent = await studentManagementClient.getStudent(student.id);
        dispatch(setStudentInfo(updatedStudent));
        dispatch(actions.setStudentLoading(false));
        dispatch(addSuccess(`Successfully updated student ${updatedStudent.firstName} ${updatedStudent.familyName}`));
        return updatedStudent;
      } catch (e) {
        dispatch(addExceptionNotification(e, 'An unknown error occurred while updating student.'));
        dispatch(actions.setStudentLoading(false));
        return null;
      }
    };

  const addStudentAsync =
    (student: StudentDto): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setStudentLoading(true));
      try {
        const id = await studentManagementClient.addStudent(student);
        studentTabsActions.removeTab(student);
        const addedStudent = await studentManagementClient.getStudent(id);
        dispatch(setStudentInfo(addedStudent));
        studentTabsActions.selectStudent(addedStudent);
        dispatch(actions.setStudentLoading(false));
        dispatch(addSuccess(`Successfully added student ${addedStudent.firstName} ${addedStudent.familyName}`));
      } catch (e) {
        dispatch(addExceptionNotification(e, 'An unknown error occurred while adding student.'));
        if (student?.semesterRegistrations) {
          for (const semesterRegistration of student.semesterRegistrations) {
            dispatch(actions.updateSemesterRegistration(semesterRegistration));
          }
        }
        dispatch(actions.setStudentLoading(false));
      }
    };

  const cancelAddingStudent = (): SyncThunkFn => (dispatch) => {
    dispatch(setStudentInfo(null));
  };

  const deleteStudentAsync =
    (student: StudentDto): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setStudentLoading(true));
      try {
        await studentManagementClient.deleteStudent(student.id);
        dispatch(setStudentInfo(null));
        dispatch(addSuccess(`Successfully deleted student ${student.firstName} ${student.familyName}`));
        dispatch(actions.setStudentLoading(false));
      } catch (e) {
        dispatch(addExceptionNotification(e, 'An unknown error occurred while deleting student.'));
        dispatch(actions.setStudentLoading(false));
      }
    };

  const updateSemesterRegistrationAsync =
    (semesterRegistration: SemesterRegistrationDto): ThunkFn =>
    async (dispatch) => {
      try {
        const result = await registrationEditClient.updateSemesterRegistration(semesterRegistration);
        dispatch(actions.updateSemesterRegistration(result));
        dispatch(addSuccess(`Successfully updated the semester registration '${semesterRegistration.semester}'.`));
      } catch (e) {
        dispatch(actions.updateSemesterRegistration(semesterRegistration));
        dispatch(addExceptionNotification(e, 'An unknown error occurred while updating the semester registration.'));
      }
    };

  const addSemesterRegistrationAsync =
    (semesterRegistration: SemesterRegistrationDto): ThunkFn =>
    async (dispatch) => {
      try {
        const result = await registrationEditClient.addSemesterRegistration(semesterRegistration);
        dispatch(actions.addSemesterRegistration(result));
        dispatch(actions.removeSemesterRegistration(semesterRegistration));
        dispatch(addSuccess(`Successfully added the semester registration '${result?.semester}'.`));
      } catch (e) {
        dispatch(actions.updateSemesterRegistration(semesterRegistration));
        dispatch(addExceptionNotification(e, 'An unknown error occurred while adding the semester registration.'));
      }
    };

  const deleteSemesterRegistrationAsync =
    (semesterRegistration: SemesterRegistrationDto): ThunkFn =>
    async (dispatch) => {
      try {
        await registrationEditClient.deleteSemesterRegistration(semesterRegistration.id);
        dispatch(actions.removeSemesterRegistration(semesterRegistration));
        dispatch(addSuccess(`Successfully deleted the semester registration ${semesterRegistration.semester}.`));
      } catch (e) {
        dispatch(addExceptionNotification(e, 'An unknown error occurred while deleting the semester registration.'));
      }
    };

  const updateModuleRegistrationAsync =
    (moduleRegistration: ModuleRegistrationDto, comment: CommentDto): ThunkFn =>
    async (dispatch) => {
      try {
        const updatedRegistration = { ...moduleRegistration };
        if (comment) {
          updatedRegistration.comments = updatedRegistration.comments ?? [];
          updatedRegistration.comments.push(comment);
        }
        const result = await registrationEditClient.updateModuleRegistration(updatedRegistration);
        dispatch(actions.updateModuleRegistration(result));
        dispatch(addSuccess(`Successfully updated the module registration.`));
      } catch (e) {
        dispatch(addExceptionNotification(e, 'An unknown error occurred while updating the module registration.'));
      }
    };

  const addModuleRegistrationAsync =
    (moduleRegistration: ModuleRegistrationDto, comment: CommentDto): ThunkFn =>
    async (dispatch) => {
      try {
        const newRegistration = { ...moduleRegistration };
        if (comment) {
          newRegistration.comments = newRegistration.comments ?? [];
          newRegistration.comments.push(comment);
        }
        const result = await registrationEditClient.addModuleRegistration(newRegistration);
        dispatch(actions.addModuleRegistration(result));
        dispatch(addSuccess(`Successfully added the module registration.`));
      } catch (e) {
        dispatch(addExceptionNotification(e, 'An unknown error occurred while adding the module registration.'));
      }
    };

  const deleteModuleRegistrationAsync =
    (moduleRegistration: ModuleRegistrationDto): ThunkFn =>
    async (dispatch) => {
      try {
        await registrationEditClient.deleteModuleRegistration(moduleRegistration.id);
        dispatch(actions.removeModuleRegistration(moduleRegistration));
        dispatch(addSuccess(`Successfully deleted the module registration.`));
      } catch (e) {
        dispatch(addExceptionNotification(e, 'An unknown error occurred while deleting the module registration.'));
      }
    };

  const updateExamRegistrationStateAsync =
    (studentId: string, examRegistrationId: number, state: ExamRegistrationState): ThunkFn =>
    async (dispatch) => {
      try {
        if (state === ExamRegistrationState.CONFIRMED) {
          await examRegistrationClient.confirmExamRegistration(examRegistrationId);
        } else if (state === ExamRegistrationState.ABSENT_EXCUSED) {
          await examRegistrationClient.withdrawExamRegistration(examRegistrationId);
        } else {
          return;
        }

        dispatch(setStudentInfo(await studentManagementClient.getStudent(studentId)));
        dispatch(addSuccess('Successfully updated the exam registration.'));
      } catch (e) {
        dispatch(addExceptionNotification(e, 'An unknown error occurred while updating the exam registration.'));
      }
    };

  const deleteResitExamRegistrationAsync =
    (studentId: string, examRegistrationId: number): ThunkFn =>
    async (dispatch) => {
      try {
        await examRegistrationClient.deleteResitExamRegistration(examRegistrationId);
        dispatch(setStudentInfo(await studentManagementClient.getStudent(studentId)));
        dispatch(addSuccess('Successfully deleted the resit exam registration.'));
      } catch (e) {
        dispatch(addExceptionNotification(e, 'An unknown error occurred while deleting the resit exam registration.'));
      }
    };

  const addCommentAsync =
    (comment: CommentDto): ThunkFn =>
    async (dispatch) => {
      try {
        const id = await registrationEditClient.addComment(comment);
        const newComment = { ...comment, id };
        dispatch(actions.addRegistrationComment(newComment));
        dispatch(addSuccess(`Successfully added comment.`));
      } catch (e) {
        dispatch(addExceptionNotification(e, 'An unknown error occurred while saving comment.'));
      }
    };

  const removeCommentAsync =
    (comment: CommentDto): ThunkFn =>
    async (dispatch) => {
      try {
        await registrationEditClient.deleteComment(comment.id);
        dispatch(actions.removeRegistrationComment(comment));
        dispatch(addSuccess(`Successfully deleted comment.`));
      } catch (e) {
        dispatch(addExceptionNotification(e, 'An unknown error occurred while deleting comment.'));
      }
    };

  const withdrawModuleRegistrationAsync =
    (moduleRegistration: ModuleRegistrationDto, comment: string): ThunkFn =>
    async (dispatch) => {
      try {
        const result = await registrationEditClient.withdrawModuleRegistration(moduleRegistration.id, comment);
        dispatch(actions.updateModuleRegistration(result));
        dispatch(addSuccess(`Successfully changed the state of the module registration.`));
      } catch (e) {
        dispatch(
          addExceptionNotification(e, 'An unknown error occurred while changing the state of the registration.'),
        );
      }
    };

  const requestModuleRegistrationAsync =
    (moduleRegistration: ModuleRegistrationDto, comment: string): ThunkFn =>
    async (dispatch) => {
      try {
        const result = await registrationEditClient.requestModuleRegistration(moduleRegistration.id, comment);
        dispatch(actions.updateModuleRegistration(result));
        dispatch(addSuccess(`Successfully changed the state of the module registration.`));
      } catch (e) {
        dispatch(
          addExceptionNotification(e, 'An unknown error occurred while changing the state of the registration.'),
        );
      }
    };

  return Object.freeze({
    cancelAddingStudent: (): void => componentDispatch(cancelAddingStudent()),
    updateSemesterRegistration: (semesterRegistration: SemesterRegistrationDto): void =>
      componentDispatch(actions.updateSemesterRegistration(semesterRegistration)),
    cancelAddingSemesterRegistration: (): void => componentDispatch(actions.cancelAddingSemesterRegistration()),
    addNewSemesterRegistration: (
      student: StudentDto,
      semesters: SemesterDto[],
      uas: UasDto[],
      profiles: ProfileDto[],
    ): SemesterRegistrationDto => componentDispatch(addNewSemesterRegistration(student, semesters, uas, profiles)),
    addNewStudent: (semesters: SemesterDto[], uas: UasDto[], profiles: ProfileDto[]): StudentDto =>
      componentDispatch(addNewStudent(semesters, uas, profiles)),
    deleteSemesterRegistrationAsync: async (semesterRegistration: SemesterRegistrationDto): Promise<void> =>
      await componentDispatch(deleteSemesterRegistrationAsync(semesterRegistration)),
    addSemesterRegistrationAsync: async (semesterRegistration: SemesterRegistrationDto): Promise<void> =>
      await componentDispatch(addSemesterRegistrationAsync(semesterRegistration)),
    updateSemesterRegistrationAsync: async (semesterRegistration: SemesterRegistrationDto): Promise<void> =>
      await componentDispatch(updateSemesterRegistrationAsync(semesterRegistration)),
    deleteModuleRegistrationAsync: async (moduleRegistration: ModuleRegistrationDto): Promise<void> =>
      await componentDispatch(deleteModuleRegistrationAsync(moduleRegistration)),
    addModuleRegistrationAsync: async (moduleRegistration: ModuleRegistrationDto, comment: CommentDto): Promise<void> =>
      await componentDispatch(addModuleRegistrationAsync(moduleRegistration, comment)),
    updateModuleRegistrationAsync: async (
      moduleRegistration: ModuleRegistrationDto,
      comment: CommentDto,
    ): Promise<void> => await componentDispatch(updateModuleRegistrationAsync(moduleRegistration, comment)),
    updateExamRegistrationStateAsync: async (
      studentId: string,
      examRegistrationId: number,
      state: ExamRegistrationState,
    ): Promise<void> => await componentDispatch(updateExamRegistrationStateAsync(studentId, examRegistrationId, state)),
    deleteResitExamRegistrationAsync: async (studentId: string, examRegistrationId: number): Promise<void> =>
      await componentDispatch(deleteResitExamRegistrationAsync(studentId, examRegistrationId)),
    addCommentAsync: async (comment: CommentDto): Promise<void> => await componentDispatch(addCommentAsync(comment)),
    removeCommentAsync: async (comment: CommentDto): Promise<void> =>
      await componentDispatch(removeCommentAsync(comment)),
    deleteEmailAsync: async (email: EmailDto): Promise<StudentDto | null> =>
      await componentDispatch(deleteEmailAsync(email)),
    loadStudentAsync: async (studentId: string): Promise<StudentDto | null> =>
      await componentDispatch(loadStudentAsync(studentId)),
    deleteStudentAsync: async (student: StudentDto): Promise<void> =>
      await componentDispatch(deleteStudentAsync(student)),
    addStudentAsync: async (student: StudentDto): Promise<void> => await componentDispatch(addStudentAsync(student)),
    updateStudentAsync: async (student: StudentDto): Promise<StudentDto | null> =>
      await componentDispatch(updateStudentAsync(student)),
    requestModuleRegistrationAsync: async (moduleRegistration: ModuleRegistrationDto, comment: string): Promise<void> =>
      await componentDispatch(requestModuleRegistrationAsync(moduleRegistration, comment)),
    withdrawModuleRegistrationAsync: async (
      moduleRegistration: ModuleRegistrationDto,
      comment: string,
    ): Promise<void> => await componentDispatch(withdrawModuleRegistrationAsync(moduleRegistration, comment)),
    getHistoryAsync,
  });
};

export type StudentManagementActions = ReturnType<typeof initActions>;

export const studentManagementModule = {
  initActions: (dispatch: any) =>
    initActions(
      dispatch,
      resolver.resolve<IStudentClient>('IStudentClient'),
      resolver.resolve<IEmailClient>('IEmailClient'),
      resolver.resolve<IRegistrationEditClient>('IRegistrationEditClient'),
      resolver.resolve<IRegistrationClient>('IRegistrationClient'),
      resolver.resolve<IExamRegistrationClient>('IExamRegistrationClient'),
      studentTabsModule.initActions(dispatch),
    ),
  reducer: studentManagementReducer,
};
